import { connect } from 'react-redux';
import { Fragment, useState, useEffect, useMemo, } from 'react';

import { siteboardAction } from '../actions/defaultActions';
import { 
  DELETE_PROJECT_SCHEDULE_CONTACT, SET_DELETE_CONTACT_DIALOGUE_OPEN, contactNeedsAnotherInvite, PUBLISH_SCHEDULE, GET_PROJECT_SCHEDULE_CONTACTS, 
  ADD_PROJECT_SCHEDULE_CONTACTS, SiteboardApiValidator, Email, SET_ALERT,
} from '../actions/types';
import { keyCountInObject, objectValuesToList, getShorthandElapsedSinceTimestamp, objectKeysToList, } from '../Util';
import { format } from 'date-fns';

import PushButton from './PushButton';
import CollapsibleContainer from './CollapsibleContainer';
import AddProjectContact from './AddProjectContact';

// icon imports
import { ContactRound, Search, XIcon, Plus, X, } from 'lucide-react';
import Logo from '../SiteBoard_Logo';
import CircleXIcon from '../icons/circleXIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';
import InfoIcon from '../icons/infoIcon';
import TeamIcon from '../icons/teamIcon';
import ClockIcon from '../icons/clockIcon';
import CloseIcon from '../icons/closeIcon';
import SendIcon from '../icons/sendIcon';

// test states
const testScrollMembers = false;
const testEmptyMembersState = false;
const testNoEmails = false;

const ProjectContacts = ({ _subscriptionContacts, _projectsContactEmails, _projects, _schedules, _userId, _subscriptions, siteboardAction, projectId, subscriptionId, scheduleId = null,  }) => {

  const selectedProject = _projects[projectId];
  const selectedSchedule = _schedules[scheduleId];
  const selectedSubscription = !selectedProject ? undefined : _subscriptions[selectedProject.subscriptionId];

  const [currentTimestampReference, setCurrentTimestampReference] = useState(null); // used to periodically trigger an update of the displayed elapsed time of messages
  const [resendingInProgressEmails, setResendingInProgressEmails] = useState({});
  const [inputListFilter, setInputListFilter] = useState('');
  const [isInviteExpanded, setIsInviteExpanded] = useState(selectedProject.contactCount === 0);
  const [emailsInput, setEmailsInput] = useState('');

  const projectContacts = useMemo(() => {
    if(testNoEmails) return [];
    if(!(subscriptionId in _subscriptionContacts)) return [];
    const projectContacts = objectKeysToList(_projectsContactEmails[projectId] || [])
      .filter((email) => email in _subscriptionContacts[subscriptionId])
      .map((email) => _subscriptionContacts[subscriptionId][email]);
    return projectContacts;
  }, [_projectsContactEmails, _subscriptionContacts, projectId, subscriptionId]);

  const filteredContacts = useMemo(() => {
    if(testNoEmails) return [];
    if(!(subscriptionId in _subscriptionContacts)) return [];
    const projectContacts = objectKeysToList(_projectsContactEmails[projectId] || [])
      .filter((email) => email in _subscriptionContacts[subscriptionId])
      .map((email) => _subscriptionContacts[subscriptionId][email]);

    const filterText = typeof inputListFilter === 'string' ? inputListFilter.trim().toLowerCase() : '';
    const filteredContacts = projectContacts.filter((contact) => {
      return contact.firstName.toLowerCase().includes(filterText) ||
        contact.lastName.toLowerCase().includes(filterText) ||
        contact.companyName.toLowerCase().includes(filterText) ||
        contact.email.toLowerCase().includes(filterText);
    });

    if(testScrollMembers) {
      const storedList = [...filteredContacts];
      for(let i = 0; testScrollMembers && i < 12; i++) {
        filteredContacts.push(...storedList);
      }
    }
    filteredContacts.sort((one, two) => {
      const companyCompare = one.companyName.toLowerCase().localeCompare(two.companyName.toLowerCase())
      if(companyCompare !== 0) return companyCompare;
      const lastNameCompare = one.lastName.toLowerCase().localeCompare(two.lastName.toLowerCase())
      if(lastNameCompare !== 0) return lastNameCompare;
      return one.email.toLowerCase().localeCompare(two.email.toLowerCase());
    });
    return filteredContacts;
  }, [_projectsContactEmails, _subscriptionContacts, inputListFilter, projectId, subscriptionId]);

  // Fetch schedule contacts from the server. If we already have them this will do nothing
  useEffect(() => {
    if(!selectedProject) return;
    siteboardAction(GET_PROJECT_SCHEDULE_CONTACTS, {projectId: selectedProject.id});
  }, [siteboardAction, selectedProject]);

  useEffect(() => {
    if(selectedProject.contactCount === 0) {
      setIsInviteExpanded(true);
    }
  }, [selectedProject.contactCount]);

  // Refresh message elapsed timestamps periodically by updating the current reference timestamp
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setCurrentTimestampReference(new Date().getTime());
    }, 20000);
    return () => { 
      clearInterval(updateInterval); // remove listener when component unloads
    };
  }, []);

  const projectContactsCount = useMemo(() => {
    if(testNoEmails) return [];
    if(!(subscriptionId in _subscriptionContacts)) return [];
    const projectContacts = objectKeysToList(_projectsContactEmails[projectId] || [])
      .filter((email) => email in _subscriptionContacts[subscriptionId])
      .map((email) => _subscriptionContacts[subscriptionId][email]);

    return projectContacts.length;
  }, [_projectsContactEmails, _subscriptionContacts, projectId, subscriptionId]);

  const getUserAvatar = (user) => {
    return (
      <div className='relative flex justify-center items-center uppercase bg-[#f99917] rounded-full p-1 w-9 h-9 min-h-9 min-w-9 text-center text-black font-semibold'>
        <div>{user.firstName[0]}{user.lastName[0]}</div>
      </div>
    );
  };

  const getContactJSX = (contact) => {
    let statusLine = <></>;
    let statusColor = 'border-[#00000000]';
    const latestDeliveryEventTimestamp = (projectId in contact.projectScheduleDeliveries) ? contact.projectScheduleDeliveries[projectId].timestamp : 0;

    if(contact.hasUnsubscribed) {
      statusColor = 'border-red-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm text-red-500'>Opted out, will not receive</div>;
    } else if(contact.hasBounce) {
      statusColor = 'border-red-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm text-red-500'>Email address does not exist</div>;
    } else if(contact.hasComplaint) {
      statusColor = 'border-red-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm text-red-500'>Flagged as spam, will not receive</div>;
    } else if(!contact.hasSubscribed && contact.latestInviteEmailTimestamp === 0) {
      // new to subscription
      statusColor = 'border-yellow-500';
      statusLine =  <div className='flex items-center gap-1.5 text-sm opacity-60'>New Contact</div>;
    } else if(!(projectId in contact.projectScheduleDeliveries)) {
      // new to project
      statusColor = 'border-yellow-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm opacity-60'>New To Project</div>;
    } else if(!contact.hasSubscribed && contact.latestInviteEmailTimestamp > 0) {
      // awaiting invite acceptance
      statusColor = 'border-yellow-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[550px]:flex-row min-[550px]:items-center min-[550px]:gap-1 text-sm opacity-60'>
            <div>Unopened, Sent {getShorthandElapsedSinceTimestamp(contact.latestInviteEmailTimestamp)} ago</div>
            <div>, {format(new Date(contact.latestInviteEmailTimestamp), 'MM/dd/yy @ h:mm aa')}</div>
          </div>
        </>
      );
    } else if(contact.hasSubscribed && contact.projectScheduleDeliveries[projectId].isDelivered === false) {
      // awaiting delivery
      statusColor = 'border-yellow-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[550px]:flex-row min-[550px]:items-center min-[550px]:gap-1 text-sm opacity-60'>
            <div>Queued to Send, {getShorthandElapsedSinceTimestamp(latestDeliveryEventTimestamp)} ago</div>
            {latestDeliveryEventTimestamp > 0 && <div>, {format(new Date(latestDeliveryEventTimestamp), 'MM/dd/yy @ h:mm aa')}</div>}
          </div>
        </>
      );
    } else if(contact.hasSubscribed && contact.projectScheduleDeliveries[projectId].isDelivered === true) {
      // delivered successfully
      statusColor = 'border-green-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[370px]:flex-row min-[370px]:items-center min-[370px]:gap-1 text-sm opacity-60'>
            <div>Sent {getShorthandElapsedSinceTimestamp(latestDeliveryEventTimestamp)} ago</div>
            {latestDeliveryEventTimestamp > 0 && <div>, {format(new Date(latestDeliveryEventTimestamp), 'MM/dd/yy @ h:mm aa')}</div>}
          </div>
        </>
      );
    }

    return (
      <div 
        key={contact.email} 
        className='flex justify-between items-center px-3 py-1.5 gap-2 hover:bg-neutral-800'
      >
        <div className={`flex flex-col grow-[1] border-l-4 ${statusColor} pl-2`}>
          {contact.hasSubscribed === true &&
            <>
              <div className='flex items-center gap-1.5'>{contact.companyName}</div>
              <div className='flex items-center gap-1.5'>{contact.firstName} {contact.lastName}</div>
            </>
          }
          <div className='break-all'>{contact.email}</div>
          {statusLine}
        </div>
        {false && selectedSchedule && contactNeedsAnotherInvite(contact) === true && 
          <PushButton
            isInline={true}
            isLoading={contact.email in resendingInProgressEmails}
            textColor='white'
            bgColor='bg-neutral-600'
            hoverBgColor='bg-neutral-500'
            title="Resend Schedule to Contact" 
            onClick={() => {
              //setResendingInProgressEmails((current) => { return {...current, [contact.email]: ''}; });
              //return;
              //siteboardAction(PUBLISH_SCHEDULE, {
              //  scheduleId: selectedSchedule.id,
              //  projectId: selectedProject.id,
              //  subscriptionId: selectedProject.subscriptionId,
              //  useEmailWhitelist: true,
              //  whitelistedEmails: [contact.email],
              //}, (output) => { 
              //});
            }}
          >
            <div className='flex items-center gap-2' >
              <SendIcon color='white' size='1em' /> 
              <div className='hidden min-[450px]:block'>Resend</div>
            </div>
          </PushButton>
        }
        <PushButton
          isInline={true}
          textColor='white'
          bgColor='bg-neutral-600'
          hoverBgColor='bg-neutral-500'
          title="Remove Contact" 
          onClick={() => {
            siteboardAction(SET_DELETE_CONTACT_DIALOGUE_OPEN, { isOpen: true, projectId: projectId, email: contact.email });
          }}
        >
          <div className='flex items-center gap-2' >
            <CloseIcon color='#f12035' size='1em' /> 
            <div className='hidden min-[450px]:block'>Remove</div>
          </div>
        </PushButton>
      </div>
    );
  };

  // const stuff = [];
  // for(let i = 100; i < 200; i++) { stuff.push(`matt${i}@test`); }
  // {stuff.map((s) => <div>{s}</div>)}

  if(!selectedProject) return <></>;

  // Contacts are loading
  if(selectedProject.contactCount !== projectContactsCount) {
    return (
      <div className={`grow flex flex-col justify-center gap-y-1 p-6`}>
        <div className='flex rounded-lg py-6 px-10 items-center justify-center font-semibold text-[1.5em] gap-x-4'>
          <div className='animate-[spin_1s_linear_infinite]'>
            <Logo size={48} />
          </div>
          <p className='select-none'>Loading Contacts</p>
        </div>
      </div>
    );
  }
  const getAddContactButtonJSX = () => {
    if(!SiteboardApiValidator[ADD_PROJECT_SCHEDULE_CONTACTS].isUserIdAuthorized(_userId, selectedSubscription, selectedProject)) return <></>;
    return (
      <PushButton
        textColor="white"
        bgColor="#471cff"
        hoverBgColor="#603aff"
        onClick={() => {
          setIsInviteExpanded(true);
        }}
      >
        <div className="flex items-center justify-center gap-2 py-1">
          <Plus size={18} />
          Add Distribution Contact
        </div>
      </PushButton>
    );
  };

  return (
    <div className='flex flex-col gap-2'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className="flex items-center gap-2">
          <div className="
            flex h-10 w-10 items-center justify-center
            rounded-lg text-[#f9a928]
          ">
            <ContactRound size='2.6em' strokeWidth={1.8} />
          </div>

          <div className="flex flex-col">
            <h3 className="text-base font-bold tracking-tight">
              Distribution List ({selectedProject.contactCount})
            </h3>
            <p className="text-sm text-white/70">
              Contacts receive published schedules via email.
            </p>
          </div>
        </div>

        {/* Wide layout - button to show invite new member component */}
        <div>
          <div className={`hidden sm:block transition-opacity duration-200 ${isInviteExpanded ? 'opacity-0' : 'opacity-100'}`}>{getAddContactButtonJSX()} </div>
        </div>
      </div>

      {/* Narrow layout - button to show invite new contact component */}
      <div className={`sm:hidden py-2`}>
        <CollapsibleContainer isOpen={!isInviteExpanded}>
          {getAddContactButtonJSX()}
        </CollapsibleContainer>
      </div>

      <CollapsibleContainer isOpen={isInviteExpanded}>
        <AddProjectContact 
          projectId={selectedProject.id}
          onClose={selectedProject.contactCount === 0 ? null : () => { setIsInviteExpanded(false) }}
        />
      </CollapsibleContainer>

      <div className='flex flex-col gap-2 shadow-lg'>
        {projectContacts.length > 0 &&
          <>
            {/* filter contacts input */}
            <div className="relative w-full">
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Search size='18' color='#aaa' />
              </div>
              <input
                type="text"
                value={inputListFilter}
                onChange={(event) => setInputListFilter(event.target.value)}
                placeholder='Search contacts...'
                className="project-detail-input pl-10"
              />
              {inputListFilter.length > 0 && (
                <button
                  type="button"
                  onClick={() => setInputListFilter('')}
                  aria-label="Clear search"
                  className="
                    absolute right-3 top-1/2 -translate-y-1/2
                    text-gray-400 transition-colors
                    hover:text-gray-100
                  "
                >
                  <XIcon size='28' />
                </button>
              )}
            </div>
            <div className='flex flex-col bg-[#333333] py-2 rounded-lg'>
              {filteredContacts.map((contact) => (
                getContactJSX(contact)
              ))}
              {projectContacts.length > 0 && filteredContacts.length === 0 &&
                <div className='px-3'>No matching contacts</div>
              }
            </div>
          </>
        }
      </div>
    </div>
  );
}

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _subscriptionContacts: state.serverSynced.subscriptionContacts,
  _projectsContactEmails: state.serverSynced.projectsContactEmails,
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, {siteboardAction})(ProjectContacts);
